import React, { useEffect, useState, useRef } from 'react'
import Api from '../../../../_helpers/Api'
import Skeleton from 'react-loading-skeleton'
import { trans } from '../../../../_providers/Translation'
import '../../../../assets/css/partials/status-diagram.css'
import { PieChart } from '@mui/x-charts'
import NoDataFound from '@/components/partials/NoDataFound'
import { useCalculateColor } from '../../../../_helpers/useCalculateColor'

export default function AdminCreatedDiagram({
    endpoint = 'dashboard/diagram/status',
    title = '',
    filters,
}) {
    const [state, setState] = useState({ loading: true, items: [] })
    const fetchDebounceRef = useRef(null)

    const { getColorVariation, gradientId, gradientUrl } = useCalculateColor('pie-gradient')

    useEffect(() => {
        clearTimeout(fetchDebounceRef.current)
        fetchDebounceRef.current = setTimeout(() => {
            const url = `${endpoint}?date_from=${filters?.date_from || ''}&date_to=${filters?.date_to || ''}`
            Api.get(url)
                .then(res => {
                    setState(prev => ({ ...prev, items: res.data.stats }))
                })
                .finally(() => {
                    setState(prev => ({ ...prev, loading: false }))
                })
        }, 300)
        return () => clearTimeout(fetchDebounceRef.current)
    }, [endpoint, filters])

    if (state.loading) {
        return (
            <div className="sd-panel">
                {/* <h4>{title}</h4> */}
                <Skeleton height={200} />
            </div>
        )
    }

    const itemsSource = Array.isArray(state.items) ? state.items : []


    const items = itemsSource.filter(i => Number(i.count || i.value || 0) > 0)

    // For each slice: color1 is the base color (used for the legend),
    // color2 is the base color lightened a bit — both feed the gradient.
    function getSliceColors(color) {
        const color1 = color
        const color2 = color ? getColorVariation(3, color) : color
        return { color1, color2 }
    }

    function formatPieChartData(items) {
        return items.map((item, index) => ({
            id: index,
            value: Number(item.count || item.value || 0),
            label: item.label || item.name,
            color: item.color ? gradientUrl(index) : item.color
        }))
    }

    // Restore the base color on the tooltip swatch (a gradient url() can't be
    // used as a background-color, so the default marker would render empty).
    function PieTooltipContent({ itemData, series }) {
        const point = series.data[itemData.dataIndex]
        const baseColor = items[itemData.dataIndex]?.color
        return (
            <div className="status-pie-tooltip" style={{
                backgroundColor: "#1E1B39",
                borderRadius: "10px"
            }}>
                <span className="marker" style={{ backgroundColor: baseColor }} />
                <span className="label">{point?.label}</span>
                <span className="value" style={{ color: "#fff" }}>{point?.value}</span>
            </div>
        )
    }

    return (
        <div className='status-pie-chart'>
            <div className='body'>
                {state.loading ?
                    <>
                        <div className='left-loading'>
                            <Skeleton height={200} count={1} />
                        </div>
                        <div className='right-loading'>
                            <Skeleton height={50} count={4} />
                        </div>
                    </>

                    : items.length === 0 ?
                        <div
                            className="alert warning"
                            style={{
                                marginBottom: 0,
                                width: '100%',
                            }}
                        >
                            {trans("messages.noDataFound")}
                        </div>

                        : <>
                            <div className='left'>
                                <PieChart
                                    series={[
                                        {
                                            data: formatPieChartData(items),
                                        },
                                    ]}
                                    sx={{
                                        '& .MuiPieArc-root': {
                                            stroke: 'none',
                                            strokeWidth: 0,
                                        },
                                    }}
                                    width={200}
                                    height={200}
                                    margin={{ right: 0 }}
                                    slotProps={{
                                        legend: { hidden: true },
                                    }}
                                    slots={{
                                        itemContent: PieTooltipContent,
                                    }}
                                >
                                    <defs>
                                        {items.map((item, index) => {
                                            const { color1, color2 } = getSliceColors(item.color)

                                            return (
                                                <linearGradient
                                                    key={index}
                                                    id={gradientId(index)}
                                                    x1="0"
                                                    y1="0"
                                                    x2="0"
                                                    y2="1"
                                                >
                                                    <stop offset="0%" stopColor={color1} />
                                                    <stop offset="100%" stopColor={color2} />
                                                </linearGradient>
                                            )
                                        })}
                                    </defs>
                                </PieChart>
                            </div>
                            <div className='right'>
                                <table>
                                    <tbody>
                                        {items.map((item, index) => (
                                            <tr key={item.id}>
                                                <td>
                                                    <span style={{
                                                        backgroundColor: item.color
                                                    }}>

                                                    </span>
                                                </td>
                                                <td>
                                                    <b>
                                                        {item.name}
                                                    </b>
                                                </td>
                                                <td>
                                                    <div className='count'>
                                                        {item.count}
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className={`percentage ${item.percentage < 0 ? 'negative' : 'positive'}`}>
                                                        {`(${item.percentage}%)`}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                }
            </div>
        </div>
    )
}